import { Bell, Trophy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface NotificationsStepProps {
  title: string;
  notificationsEnabled: boolean;
  onEnableNotifications: () => void;
  onSkip: () => void;
}

export function NotificationsStep({
  title,
  notificationsEnabled,
  onEnableNotifications,
  onSkip,
}: NotificationsStepProps) {
  const [isRequesting, setIsRequesting] = useState(false);

  const handleEnable = async () => {
    setIsRequesting(true);
    try {
      await onEnableNotifications();
    } finally {
      // Give time for the parent to update notificationsEnabled
      setTimeout(() => setIsRequesting(false), 500);
    }
  };

  return (
    <div className="w-full max-w-sm animate-fade-in space-y-6">
      <div className="text-center">
        <div className="w-20 h-20 rounded-full evolve-gradient flex items-center justify-center mx-auto mb-6 pulse-glow">
          {notificationsEnabled ? (
            <Check className="w-10 h-10 text-foreground" />
          ) : (
            <Bell className="w-10 h-10 text-foreground" />
          )}
        </div>
        <h1 className="font-display text-2xl font-bold mb-2">{title}</h1>
      </div>

      {/* User's name with tier badge */}
      <div className="flex items-center justify-center gap-2 mb-2">
        <div className="bg-gradient-to-r from-gray-600 to-gray-500 px-3 py-1 rounded-full">
          <span className="text-xs font-bold text-foreground flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            Evolver Seed
          </span>
        </div>
      </div>

      {/* Main message */}
      <div className="bg-secondary/30 border border-border/50 rounded-xl p-5 space-y-4 text-center">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Notifications keep you accountable with <span className="text-foreground font-medium">daily gym reminders</span>, <span className="text-foreground font-medium">weigh-in alerts</span>, and motivation to hit <span className="text-primary font-semibold">Legend</span>.
        </p>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
          <p className="text-sm text-primary font-semibold">
            Evolvers with notifications are 4.7× more likely to succeed.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          Just push notifications — no spam, ever.
        </p>
      </div>

      {/* Success message when enabled */}
      {notificationsEnabled && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 animate-fade-in">
          <p className="text-sm text-green-400 font-medium text-center">
            ✅ Legend—your nudges are live. First one at your training time tomorrow.
          </p>
        </div>
      )}

      {/* Action buttons */}
      {!notificationsEnabled && (
        <div className="space-y-3">
          <Button
            variant="evolve"
            onClick={handleEnable}
            disabled={isRequesting}
            className="w-full py-6 text-base font-bold"
          >
            {isRequesting ? (
              <>
                <div className="w-5 h-5 mr-2 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
                Enabling...
              </>
            ) : (
              <>
                <Bell className="w-5 h-5 mr-2" />
                Enable Now
              </>
            )}
          </Button>
          <button
            onClick={onSkip}
            disabled={isRequesting}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2 disabled:opacity-50"
          >
            Skip for Now
          </button>
        </div>
      )}
    </div>
  );
}
